import type {
  DomainCanvas,
  DomainDocument,
  KnowledgeItem,
} from "@note-app/contracts";
import type { Folder, RevisionRecord, Tag } from "@note-app/domain";
import type { AttachmentRecord } from "@note-app/knowledge-services";
import type { DBSchema } from "idb";

export const INDEXED_DB_SCHEMA_VERSION = 3;

export interface StoredDocumentRow {
  itemId: string;
  revision: number;
  contentRevision: number;
  document: DomainDocument;
}

export interface StoredCanvasRow {
  itemId: string;
  revision: number;
  contentRevision: number;
  canvas: DomainCanvas;
}

export interface ChangeRecord {
  sequence?: number;
  entityId: string;
  entityType:
    | "item"
    | "document"
    | "canvas"
    | "folder"
    | "tag"
    | "attachment";
  operation: "create" | "update" | "save" | "trash" | "restore" | "delete";
  revision: number;
  committedAt: number;
  payload: unknown;
}

export interface BlobRow {
  storageKey: string;
  bytes: Uint8Array;
  byteLength: number;
  modifiedAt: number;
}

export interface MetaRow {
  key: string;
  value: unknown;
}

export interface KnowledgeDbSchema extends DBSchema {
  meta: {
    key: string;
    value: MetaRow;
  };
  items: {
    key: string;
    value: KnowledgeItem;
    indexes: {
      "by-updated": [number, string];
      "by-kind": string;
      "by-folder": string;
    };
  };
  documents: {
    key: string;
    value: StoredDocumentRow;
  };
  canvases: {
    key: string;
    value: StoredCanvasRow;
  };
  folders: {
    key: string;
    value: Folder;
    indexes: { "by-name": [string, string] };
  };
  tags: {
    key: string;
    value: Tag;
    indexes: { "by-name": [string, string] };
  };
  revisions: {
    key: string;
    value: RevisionRecord;
    indexes: {
      "by-item": string;
      "by-item-revision": [string, number];
    };
  };
  changes: {
    key: number;
    value: ChangeRecord;
    indexes: {
      "by-entity": string;
      "by-committed": number;
    };
  };
  attachments: {
    key: string;
    value: AttachmentRecord;
    indexes: { "by-sha256": string };
  };
  blobs: {
    key: string;
    value: BlobRow;
  };
}

export type KnowledgeStoreName =
  | "meta"
  | "items"
  | "documents"
  | "canvases"
  | "folders"
  | "tags"
  | "revisions"
  | "changes"
  | "attachments"
  | "blobs";

export const ALL_KNOWLEDGE_STORES: readonly KnowledgeStoreName[] = [
  "meta",
  "items",
  "documents",
  "canvases",
  "folders",
  "tags",
  "revisions",
  "changes",
  "attachments",
  "blobs",
];
